import type { App } from 'vue';
import type { Router } from 'vue-router';
import * as Sentry from '@sentry/vue';
import { env } from '@/shared/config/env';
import { logger } from './logger';

/**
 * A real Sentry DSN looks like `https://<key>@<host>/<projectId>`. We only
 * init when the value is a usable URL — placeholder strings (e.g.
 * `PLACEHOLDER_PRODUCTION_DSN`) would otherwise throw inside Sentry.init and
 * crash the whole app at boot, leaving the user with a blank page.
 */
function isValidDsn(value: string): boolean {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function initSentry(app: App, router: Router): void {
  if (!isValidDsn(env.sentryDsn)) {
    logger.info(
      { event: 'sentry.skip', reason: env.sentryDsn ? 'invalid_dsn' : 'no_dsn' },
      'Sentry DSN not configured — skipping init',
    );
    return;
  }
  try {
    Sentry.init({
      app,
      dsn: env.sentryDsn,
      environment: env.sentryEnvironment,
      release: `cc-admin@${env.appVersion}`,
      integrations: [Sentry.browserTracingIntegration({ router })],
      tracesSampleRate: env.sentryEnvironment === 'production' ? 0.2 : 1.0,
      sendDefaultPii: false,
    });
    logger.info(
      { event: 'sentry.init', environment: env.sentryEnvironment },
      'Sentry initialised',
    );
  } catch (err) {
    // Never let Sentry crash the app — the dashboard must boot even if telemetry fails.
    logger.warn({ event: 'sentry.init_failed', err }, 'Sentry init failed — continuing without it');
  }
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!isValidDsn(env.sentryDsn)) return;
  try {
    Sentry.captureException(error, { extra: context });
  } catch {
    /* never let Sentry crash callers */
  }
}

export function setSentryUser(user: { id: number; phone_number?: string | null } | null): void {
  if (!isValidDsn(env.sentryDsn)) return;
  try {
    Sentry.setUser(user ? { id: String(user.id) } : null);
  } catch {
    /* never let Sentry crash callers */
  }
}
